import axios from 'axios';
import { TenantAuthorizationData } from '../../../../context/auth';
import {
	AutofillIntegrationCreateRequest,
	AutofillIntegrationResponse,
	ExternalServiceIntegrationAddRequest,
	ExternalServiceIntegrationsResponse,
	ExternalServiceIntegrationUpdateRequest,
} from '../../tenant/api/integrations';
import {
	ScriptLogRequest,
	ScriptLogResponse,
} from '../../tenant/api/scriptLogs';
import {
	GetWebhookResponse,
	WebhookRequest,
	WebhookResponse,
} from '../../tenant/api/webhooks';
import { AuthorizeTenantRequest, Tenant } from '../types';
import apiClient from './apiClient';

const TENANTS_KEY = 'tenants';

const getTenants = async () => {
	const response = await apiClient.get<Tenant[]>('/auth/tenants');
	return response.data;
};

const deployTenant = async (
	request: FormData,
	uploadProgressCallback?: (progressEvent: number) => void,
) => {
	const response = await axios.post('/loadassets/assets/v1/deploy', request, {
		headers: { 'Content-Type': 'multipart/form-data' },
		onUploadProgress: (progressEvent) => {
			const percentCompleted = Math.round(
				(progressEvent.loaded * 100) / progressEvent.total,
			);
			uploadProgressCallback(percentCompleted);
		},
	});
	return response.data;
};

const deployTenantWithPV = async (
	request: FormData,
	uploadProgressCallback?: (progressEvent: number) => void,
) => {
	const response = await axios.post(
		'/loadassets/configuration/deploy',
		request,
		{
			headers: { 'Content-Type': 'multipart/form-data' },
			onUploadProgress: (progressEvent) => {
				const percentCompleted = Math.round(
					(progressEvent.loaded * 100) / progressEvent.total,
				);
				uploadProgressCallback(percentCompleted);
			},
		},
	);
	return response.data;
};

const authorizeTenant = async (
	body: AuthorizeTenantRequest,
): Promise<TenantAuthorizationData> => {
	const res = await apiClient.post(
		`/account/v1/authorization/update/tenant`,
		body,
	);

	return res.data;
};

const getScriptLogs = async (request: ScriptLogRequest, hostname: string) => {
	const res = await apiClient.post<ScriptLogResponse>(
		'/scripts/queryLogs?ns_tenant_host=' + hostname,
		request,
	);

	return res.data;
};

const getIntegrations = async (tenantHostname: string) => {
	const res = await apiClient.get<ExternalServiceIntegrationsResponse>(
		'/integrations?ns_tenant_host=' + tenantHostname,
	);

	return res.data;
};

const getDataAutofills = async (tenantHostname?: string) => {
	const res = await apiClient.get<AutofillIntegrationResponse>(
		'/integrations/autofill?ns_tenant_host=' + tenantHostname,
	);

	return res.data;
};

const addIntegration = async (
	request: ExternalServiceIntegrationAddRequest,
	tenantHostname: string,
) => {
	const res = await apiClient.post<ExternalServiceIntegrationsResponse>(
		'/integrations?ns_tenant_host=' + tenantHostname,
		request,
	);

	return res.data;
};

const editIntegration = async (
	request: ExternalServiceIntegrationUpdateRequest,
	integrationName: string,
	tenantHostname: string,
) => {
	const res = await apiClient.put<ExternalServiceIntegrationsResponse>(
		`/integrations/${integrationName}?ns_tenant_host=${tenantHostname}`,
		request,
	);

	return res.data;
};

const deleteIntegration = async (
	integrationName: string,
	tenantHostname: string,
) => {
	const res = await apiClient.delete<ExternalServiceIntegrationsResponse>(
		`/integrations/${integrationName}?ns_tenant_host=${tenantHostname}`,
	);

	return res.data;
};

const addUpdateDataAutofill = async (
	request: AutofillIntegrationCreateRequest,
	tenantHostname: string,
) => {
	const res = await apiClient.put<ExternalServiceIntegrationsResponse>(
		'/integrations/autofill?ns_tenant_host=' + tenantHostname,
		request,
	);

	return res.data;
};

const deleteDataAutofill = async (locator: string, tenantHostname: string) => {
	const res = await apiClient.delete<void>(
		`/integrations/autofill/${locator}?ns_tenant_host=${tenantHostname}`,
	);

	return res.data;
};

const getWebhooks = async (tenantHostname: string) => {
	const res = await apiClient.get<GetWebhookResponse>(
		'/webhooks?ns_tenant_host=' + tenantHostname,
	);

	return res.data;
};

const configureWebhook = async (
	request: WebhookRequest,
	tenantHostname: string,
) => {
	const res = await apiClient.put<WebhookResponse>(
		'/webhooks?ns_tenant_host=' + tenantHostname,
		request,
	);

	return res.data;
};

const deleteWebhook = async (name: string, tenantHostname: string) => {
	await apiClient.delete(`/webhooks/${name}?ns_tenant_host=${tenantHostname}`);
};

const unsuspendWebhook = async (name: string, tenantHostname: string) => {
	const res = await apiClient.patch<WebhookResponse>(
		`/webhooks/${name}/unsuspend?ns_tenant_host=${tenantHostname}`,
		{},
	);

	return res.data;
};

const getEventTypes = async (tenantHostname: string) => {
	const res = await apiClient.get(
		`/events/types?ns_tenant_host=${tenantHostname}`,
	);

	return res.data;
};

const TenantsAPI = {
	authorizeTenant,
	getTenants,
	deployTenant,
	deployTenantWithPV,
	getScriptLogs,
	getIntegrations,
	getDataAutofills,
	addIntegration,
	editIntegration,
	deleteIntegration,
	addUpdateDataAutofill,
	deleteDataAutofill,
	getWebhooks,
	configureWebhook,
	deleteWebhook,
	unsuspendWebhook,
	getEventTypes,
	TENANTS_KEY,
};

export default TenantsAPI;
